import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import * as React from 'react';
import styled from 'styled-components';
import useDeepCompareEffect from 'use-deep-compare-effect';

import { followedSearchPageItemCard } from '@/components/GridItemCard';
import FluidItemGrid from '@/components/GridItemCard/FluidItemGrid/FluidItemGrid';
import { MyFavoritesNoResults } from '@/components/MyFavoritesNoResults/MyFavoritesNoResults';
import Pagination from '@/components/Pagination/hammer-pagination/Pagination';
import { PaginationFilter } from '@/components/Pagination/hammer-pagination/types/PaginationFilter';
import SearchFollowSuggestions from '@/components/SearchFollowSuggestions/SearchFollowSuggestions';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { getMySavedSearchItemIds } from '@/redux/modules/filteredItems';
import { fetchItemSummariesIfNeeded } from '@/redux/modules/item/summary/itemSummary.actions';
import {
    getTotalSavedSearches,
    getTotalSavedSearchItems,
    isSavedSearchItemsLoading,
} from '@/redux/modules/savedSearch';

import SavedSearchFilter from './SavedSearchFilter';

type Props = {
    filters: PaginationFilter;
    onSavedSearchRecentFilterChange: (filters: PaginationFilter) => void;
};

const MySavedSearchPageRecentItems = (props: Props) => {
    const dispatch = useAppDispatch();
    const { filters, onSavedSearchRecentFilterChange } = props;

    const isLoading = useAppSelector(isSavedSearchItemsLoading);
    const itemIds = useAppSelector((state) =>
        getMySavedSearchItemIds(state, {
            count: filters.pageSize,
            page: filters.page || 1,
            terms: filters.keyword,
        })
    );
    const totalSavedSearchesCount = useAppSelector(getTotalSavedSearches);
    const totalSavedSearchItemsCount = useAppSelector(getTotalSavedSearchItems);
    const showPager = typeof filters.pageSize === 'number' && totalSavedSearchItemsCount > filters.pageSize;

    useDeepCompareEffect(() => {
        dispatch(
            fetchItemSummariesIfNeeded({
                identifier: 'MY-SAVED-SEARCH-PAGE',
                itemIds,
            })
        );
    }, [dispatch, itemIds]);

    const filterComponent = (
        <SavedSearchFilter
            filters={filters}
            onFilterChange={onSavedSearchRecentFilterChange}
        />
    );

    const gridComponent = (
        <StyledFluidItemGrid
            itemCardOption={followedSearchPageItemCard}
            itemIds={itemIds}
        />
    );

    return (
        <div data-testid="my-saved-search-page-recent-items">
            {showPager ? (
                <Pagination
                    filter={filterComponent}
                    onChange={onSavedSearchRecentFilterChange}
                    paginationFilter={{
                        ...filters,
                        totalRecords: totalSavedSearchItemsCount,
                    }}
                    shouldModifyQueryParams
                >
                    {gridComponent}
                </Pagination>
            ) : (
                <>
                    {filterComponent}
                    {!isLoading && !itemIds.length ? (
                        <MyFavoritesNoResults>
                            <FormattedMessage id="empty_recent_results_message_part_1" />
                        </MyFavoritesNoResults>
                    ) : (
                        gridComponent
                    )}
                </>
            )}

            {!isLoading && totalSavedSearchesCount === 0 && (
                <NoFollowed>
                    <FormattedMessage id="search_alerts.no_followed" />
                </NoFollowed>
            )}
            {totalSavedSearchesCount <= 0 && <StyledSearchFollowSuggestions useAltHeader />}
        </div>
    );
};

export default MySavedSearchPageRecentItems;

const NoFollowed = styled.div`
    margin: 32px 0;
    text-align: center;
    size: 14px;
    letter-spacing: 0.23px;
`;

const StyledSearchFollowSuggestions = styled(SearchFollowSuggestions)`
    width: 100%;

    & > div[class*='side-scroller'] {
        width: 100%;
    }

    & > div[class*='altHeader'] {
        margin: 0 0 20px;
    }
`;

const StyledFluidItemGrid = styled(FluidItemGrid)`
    margin-top: 32px;
    @media (max-width: ${({ theme }) => theme.breakpoints.mobileWidth}) {
        margin-bottom: 16px;
    }
`;
